// Apolo IDE — Hermes Team Mode: chat de 1 turno SEM tools (plan + síntese).
//
// Adapter fino sobre o resolveBrain existente (mesmo split local/cloud do
// resto do app) com timeout DURO via callWithTimeout (REUSO — agentLoop.js).
// O port `chat` que planTeam/synthesizeTeam recebem é isto aqui, injetado no
// composition root (runWiring) — a application nunca importa src/lib.
//
// Contrato: chat({ brain, system, user, signal, timeoutMs, onToken,
//                  temperature, top_p, onCall })
//   → { ok, content?, error? } — NUNCA lança (timeout/abort viram { ok:false }).
//
// Squad Orchestrator (E1/E2):
//   - temperature/top_p: passados ao chatFn via options (structurer usa temp 0
//     → determinismo do passo de estruturação). Default = comportamento atual.
//   - onCall({ model, temperature, top_p, contextChars }): hook best-effort
//     chamado ANTES de cada chamada LLM com os parâmetros + o tamanho do
//     contexto (chars de system+user) — o squad_call_log (E2) consome pra
//     gravar prompt_hash + context_tokens. NUNCA quebra a chamada.

import { resolveBrain as realResolveBrain } from '../../lib/brainRouter.js';
import { callWithTimeout, LLM_CALL_TIMEOUT_MS } from '../../lib/agentLoop.js';

export function makeTeamChat({ resolveBrain = realResolveBrain, withTimeout = callWithTimeout } = {}) {
  return async function chat({
    brain,
    system,
    user,
    signal,
    timeoutMs = LLM_CALL_TIMEOUT_MS,
    onToken,
    temperature,
    top_p,
    onCall,
  } = {}) {
    let resolved;
    try {
      resolved = resolveBrain(brain || {}, { dispatchId: 'team-chat' });
    } catch (e) {
      return { ok: false, error: e?.message || 'brain inválido' };
    }

    const messages = [
      ...(system ? [{ role: 'system', content: system }] : []),
      { role: 'user', content: String(user ?? '') },
    ];

    // onCall best-effort ANTES da chamada — observabilidade nunca atrapalha o turno.
    if (typeof onCall === 'function') {
      try {
        onCall({
          model: resolved.model,
          temperature: typeof temperature === 'number' ? temperature : undefined,
          top_p: typeof top_p === 'number' ? top_p : undefined,
          contextChars: (system ? String(system).length : 0) + String(user ?? '').length,
          system,
          user: String(user ?? ''),
        });
      } catch {
        /* observabilidade é best-effort */
      }
    }

    // options só carrega o que foi explicitamente pedido — preserva o default
    // (objeto vazio) quando temperature/top_p não vêm, então o comportamento
    // atual fica byte-idêntico.
    const options = {};
    if (typeof temperature === 'number') options.temperature = temperature;
    if (typeof top_p === 'number') options.top_p = top_p;

    // Timeout e Stop do usuário compartilham um controller interno: o outer
    // signal propaga pra cá, e o onTimeout aborta a chamada em voo (o fetch
    // cloud respeita; o IPC local ignora o signal mas o callWithTimeout ainda
    // resolve a corrida). Espelha o pattern do agentLoop (E4-T01).
    const ctrl = new AbortController();
    const onOuterAbort = () => ctrl.abort();
    if (signal) {
      if (signal.aborted) ctrl.abort();
      else signal.addEventListener('abort', onOuterAbort, { once: true });
    }

    try {
      const onDelta = onToken
        ? (d) => {
            if (d?.type === 'token' && typeof d.delta === 'string') onToken(d.delta);
          }
        : undefined;
      const res = await withTimeout(
        () =>
          resolved.chatFn(
            { baseUrl: resolved.baseUrl, model: resolved.model, messages, options },
            onDelta,
            ctrl.signal,
          ),
        timeoutMs,
        { onTimeout: () => ctrl.abort() },
      );
      if (!res || typeof res !== 'object') return { ok: false, error: 'resposta inválida do brain' };
      return res;
    } catch (e) {
      return { ok: false, error: e?.message || String(e) };
    } finally {
      if (signal) signal.removeEventListener('abort', onOuterAbort);
    }
  };
}
